import { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, List, ListItem, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import { AssetSidebar } from '../AssetSidebar';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { SIDEBAR_WIDTH } from '@/theme';
import { createUseStyles } from '@/services/createUseStyles';
import { AdvancedAnalysisSidebar } from './AdvancedAnalysisSidebar';
import { useToast } from '@/hooks/useToast';
import { useGetAdvancedAnalysis } from '@/hooks/api/advanced-analysis/useGetAdvancedAnalysis';
import { usePostChat } from '@/hooks/api/advanced-analysis/usePostChat';
import { useTenants } from '@/services/tenants/useTenants';

interface AdvancedAnalysisChatProperties {
  assetId?: string;
}

const useStyles = createUseStyles(theme => ({
  AdvancedAnalysisChat_root: {
    display: 'flex',
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  AdvancedAnalysisChat_content: {
    height: '100%',
    padding: theme.spacing(2),
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
    transition: 'margin-left 0.2s ease-in-out',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',

    [theme.breakpoints.down('md')]: {
      marginLeft: '0 !important',
      width: '100%',
    },
  },
  AdvancedAnalysisChat_container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    maxWidth: '1000px',
  },
  AdvancedAnalysisChat_title: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  AdvancedAnalysisChat_messagesContainer: {
    flex: 1,
    overflow: 'auto',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    maxHeight: '60vh',
    backgroundColor: theme.palette.background.default,
    width: '100%',
    boxSizing: 'border-box',
  },
  AdvancedAnalysisChat_messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  AdvancedAnalysisChat_messageItem: {
    background: 'none',
    padding: theme.spacing(0, 2),
  },
  AdvancedAnalysisChat_messageItem_user: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  AdvancedAnalysisChat_messageItem_assistant: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  AdvancedAnalysisChat_userMessage: {
    padding: theme.spacing(0, 1),
    borderRadius: +theme.shape.borderRadius * 3,
    maxWidth: '60%',
    backgroundColor: theme.palette.background.default,
    marginLeft: 'auto',
    alignSelf: 'flex-end',
  },
  AdvancedAnalysisChat_assistantMessage: {
    maxWidth: '80%',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
  AdvancedAnalysisChat_inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(0, 2),
  },
  AdvancedAnalysisChat_sendButton: {
    minWidth: '100px',
  },
}));

interface ChatMessage {
  sender: 'user' | 'assistant';
  text: string;
  isStreaming?: boolean;
}

const isUserMessage = (message: ChatMessage) => message.sender === 'user';

const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    const form = event.currentTarget.closest('form');
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }
};

export const AdvancedAnalysisChat = ({ assetId }: AdvancedAnalysisChatProperties) => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_WIDTH);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(500);
  const { asset } = useGetAsset({ assetId: assetId ?? '' });
  const c = useStyles();
  const messagesEndReference = useRef<HTMLDivElement | null>(null);
  const { showToast } = useToast();
  const { getTenantId } = useTenants();

  const {
    analysis,
    isFetching: isAnalysisFetching,
    error: analysisError,
    refetch: refetchAnalysis,
  } = useGetAdvancedAnalysis({
    assetId,
  });

  const { mutate: postChat, isPending: isChatPending } = usePostChat();

  useEffect(() => {
    if (analysisError) {
      showToast({ message: 'Failed to fetch AI analysis', type: 'error' });
    }
  }, [analysisError]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!message.trim() || !assetId) return;

    setChatHistory(previous => [...previous, { sender: 'user', text: message }]);
    setMessage('');

    setChatHistory(previous => [...previous, { sender: 'assistant', text: '', isStreaming: true }]);

    postChat(
      {
        assetId,
        tenantId: getTenantId(),
        message,
        onChunk: handleChatChunk,
      },
      {
        onSuccess: finishChatSuccess,
        onError: finishChatError,
      }
    );
  };

  const handleChatChunk = (chunk: string) => {
    setChatHistory(previous => {
      const lastMessage = previous.at(-1);
      if (lastMessage?.isStreaming) {
        return [...previous.slice(0, -1), { ...lastMessage, text: chunk }];
      }
      return previous;
    });
  };

  const finishChatSuccess = () => {
    setChatHistory(previous =>
      previous.map(message_ => (message_.isStreaming ? { ...message_, isStreaming: false } : message_))
    );
    refetchAnalysis();
  };

  const finishChatError = () => {
    setChatHistory(previous =>
      previous.map(message_ =>
        message_.isStreaming
          ? {
              sender: 'assistant',
              text: 'Sorry, there was an error processing your request.',
              isStreaming: false,
            }
          : message_
      )
    );
  };

  useEffect(() => {
    messagesEndReference.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  return (
    <Box className={c.AdvancedAnalysisChat_root}>
      <AssetSidebar asset={asset} onWidthChange={setSidebarWidth} />
      <Box
        className={c.AdvancedAnalysisChat_content}
        style={{ marginLeft: sidebarWidth, marginRight: rightSidebarWidth }}
      >
        <Box className={c.AdvancedAnalysisChat_container}>
          <Typography variant='h4' component='h1' className={c.AdvancedAnalysisChat_title}>
            Advanced Analysis
          </Typography>
          <Paper elevation={2} className={c.AdvancedAnalysisChat_messagesContainer}>
            <List className={c.AdvancedAnalysisChat_messageList}>
              {chatHistory.map((message_, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  className={[
                    c.AdvancedAnalysisChat_messageItem,
                    isUserMessage(message_)
                      ? c.AdvancedAnalysisChat_messageItem_user
                      : c.AdvancedAnalysisChat_messageItem_assistant,
                  ].join(' ')}
                >
                  {isUserMessage(message_) ? (
                    <Paper elevation={1} className={c.AdvancedAnalysisChat_userMessage}>
                      <ReactMarkdown>{message_.text}</ReactMarkdown>
                    </Paper>
                  ) : (
                    <Box className={c.AdvancedAnalysisChat_assistantMessage}>
                      <Box sx={{ wordBreak: 'break-word' }}>
                        <ReactMarkdown>{message_.text}</ReactMarkdown>
                      </Box>
                      {message_.isStreaming && <CircularProgress size={16} sx={{ ml: 1, mt: 0.5 }} />}
                    </Box>
                  )}
                </ListItem>
              ))}
              <div ref={messagesEndReference} />
            </List>
          </Paper>
          <Box
            component='form'
            onSubmit={event => void handleSubmit(event)}
            className={c.AdvancedAnalysisChat_inputContainer}
          >
            <TextField
              fullWidth
              value={message}
              onChange={event => setMessage(event.target.value)}
              placeholder='Type your message...'
              variant='outlined'
              size='small'
              multiline={true}
              rows={2}
              onKeyDown={handleKeyDown}
            />
            <Button
              type='submit'
              variant='contained'
              className={c.AdvancedAnalysisChat_sendButton}
              endIcon={isChatPending ? <CircularProgress size={20} color='inherit' /> : <SendIcon />}
              disabled={isChatPending || !message.trim()}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Box>
      {assetId && (
        <AdvancedAnalysisSidebar
          onWidthChange={setRightSidebarWidth}
          analysis={analysis ?? {}}
          isLoading={isAnalysisFetching || isChatPending}
          asset={asset}
        />
      )}
    </Box>
  );
};
